import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * A horizontal scrollbar pinned to the bottom of the viewport that mirrors one
 * or more horizontally-overflowing scroll containers.
 *
 * It fixes the "to scroll a wide table sideways you must first scroll all the
 * way down to reach its native scrollbar" problem: the floating bar is always
 * within reach while any part of the table is on screen.
 *
 * Purely ADDITIVE — it never changes a table's own layout, sizing or vertical
 * scrolling. It renders nothing (and so costs nothing) whenever it isn't
 * needed: no horizontal overflow, the target's own scrollbar is already on
 * screen (you've scrolled to the bottom), or the target is out of view.
 *
 * `containerRef` points at a wrapping element. Every descendant carrying the
 * `data-fd-xscroll` attribute — or the container itself if it has it — is a
 * synced scroll target. When several targets are present (e.g. one per Task
 * Monitor status section) they share ONE horizontal position, so scrolling any
 * one of them (or the floating bar) moves them all and their columns stay
 * aligned.
 *
 * `zIndex` optionally overrides the stacking order (default comes from the
 * `.fd-floating-xscroll` CSS class, which sits below the app's overlays). Pass
 * a higher value when the host renders the table above those overlays — e.g.
 * DataTable's full-screen mode, whose own layer would otherwise occlude it.
 */
export function FloatingXScrollbar({ containerRef, zIndex }) {
  const barRef = useRef(null)
  // The single horizontal scroll position shared by the bar + every target.
  const syncedXRef = useRef(0)
  // Last snapshot pushed to state — lets the high-frequency scroll handler skip
  // no-op setState so it doesn't re-render on every pixel.
  const lastRef = useRef(null)
  const [snap, setSnap] = useState(null)

  const collectTargets = (container) => {
    if (!container) return []
    const out = []
    if (container.matches?.('[data-fd-xscroll]')) out.push(container)
    container.querySelectorAll?.('[data-fd-xscroll]').forEach((el) => out.push(el))
    return out
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container || typeof window === 'undefined') return undefined

    // The target that drives the bar's position/size: the overflowing, on-screen
    // one with the largest visible area whose OWN scrollbar is still below the
    // viewport (otherwise the native bar is already reachable — hide ours).
    const pickDriver = (targets) => {
      let best = null
      let bestArea = -1
      for (const el of targets) {
        if (el.scrollWidth - el.clientWidth <= 1) continue
        const r = el.getBoundingClientRect()
        if (r.bottom <= window.innerHeight) continue
        if (r.top >= window.innerHeight || r.bottom <= 0) continue
        const visible = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0)
        if (visible > bestArea) {
          bestArea = visible
          best = el
        }
      }
      return best
    }

    let raf = 0
    const measure = () => {
      raf = 0
      const targets = collectTargets(container)

      // Shared-position sync: if any target diverged from the last synced X, the
      // user scrolled it — adopt that as the new position and push it to every
      // other target + the bar, so all sections and the thumb stay aligned no
      // matter which one received the gesture. Equal values are skipped, so this
      // converges in one pass and fires no feedback loop.
      let sourceX = syncedXRef.current
      for (const el of targets) {
        if (Math.abs(el.scrollLeft - syncedXRef.current) > 0.5) {
          sourceX = el.scrollLeft
          break
        }
      }
      if (Math.round(sourceX) !== Math.round(syncedXRef.current)) {
        syncedXRef.current = sourceX
        for (const el of targets) {
          if (Math.round(el.scrollLeft) !== Math.round(sourceX)) el.scrollLeft = sourceX
        }
        if (barRef.current && Math.round(barRef.current.scrollLeft) !== Math.round(sourceX)) {
          barRef.current.scrollLeft = sourceX
        }
      }

      const el = pickDriver(targets)
      if (!el) {
        if (lastRef.current !== null) {
          lastRef.current = null
          setSnap(null)
        }
        return
      }
      const r = el.getBoundingClientRect()
      const next = {
        left: Math.round(r.left),
        width: Math.round(r.width),
        scrollWidth: Math.round(el.scrollWidth),
      }
      const prev = lastRef.current
      if (
        !prev ||
        prev.left !== next.left ||
        prev.width !== next.width ||
        prev.scrollWidth !== next.scrollWidth
      ) {
        lastRef.current = next
        setSnap(next)
      }
    }
    const schedule = () => {
      if (!raf) raf = window.requestAnimationFrame(measure)
    }

    // ResizeObserver catches layout changes. We observe the container, every
    // target, AND each target's inner element — horizontal overflow is driven
    // by the inner table, whose width can change (show/hide columns) without
    // resizing the clipping box, so observing only the container misses it.
    const ro = new ResizeObserver(schedule)
    const observeAll = () => {
      ro.observe(container)
      for (const el of collectTargets(container)) {
        ro.observe(el)
        if (el.firstElementChild) ro.observe(el.firstElementChild)
      }
    }
    observeAll()
    // Re-observe when targets mount/unmount after the first render (e.g. a Task
    // Monitor section expands, or rows/columns load later).
    const mo = new MutationObserver(() => {
      observeAll()
      schedule()
    })
    mo.observe(container, { childList: true, subtree: true })

    schedule()
    // `scroll` with capture:true catches BOTH page scroll and inner target
    // scroll (scroll doesn't bubble, but it IS seen in the capture phase).
    window.addEventListener('scroll', schedule, { passive: true, capture: true })
    window.addEventListener('resize', schedule)

    return () => {
      if (raf) window.cancelAnimationFrame(raf)
      ro.disconnect()
      mo.disconnect()
      window.removeEventListener('scroll', schedule, { capture: true })
      window.removeEventListener('resize', schedule)
    }
  }, [containerRef])

  // When the bar (re)appears, align its thumb to the current shared position:
  // the measure() that flipped `snap` from null ran before the portal existed,
  // so it couldn't set the (not-yet-mounted) bar's scrollLeft.
  useLayoutEffect(() => {
    if (snap && barRef.current) barRef.current.scrollLeft = syncedXRef.current
  }, [snap])

  const onBarScroll = () => {
    const x = barRef.current?.scrollLeft ?? 0
    if (Math.round(x) === Math.round(syncedXRef.current)) return
    syncedXRef.current = x
    for (const el of collectTargets(containerRef.current)) {
      if (Math.round(el.scrollLeft) !== Math.round(x)) el.scrollLeft = x
    }
  }

  if (!snap) return null
  return createPortal(
    <div
      ref={barRef}
      aria-hidden="true"
      onScroll={onBarScroll}
      className="fd-floating-xscroll"
      style={{ left: snap.left, width: snap.width, ...(zIndex != null ? { zIndex } : null) }}
    >
      <div style={{ width: snap.scrollWidth, height: 1 }} />
    </div>,
    document.body,
  )
}
